import { useState } from "preact/hooks";
import type { DashboardViewModel } from "../app/dashboardViewModel";
import type { DialDirection, DialFormat, Grinder } from "../engine/model";
import { parseNumber } from "../app/format";
import { formatGrind } from "../app/grindFormat";
import { GRINDER_PRESETS, UNKNOWN_SLOPE, presetById } from "../app/grinderPresets";
import { Modal } from "./dialogs";

/**
 * Manage grinders: list, edit, add. Adding asks for the name, dial format, range, dial step, which way is coarser,
 * and a seed slope from a short preset list ("Unknown" seeds −0.20 and recommends a calibration sweep). Format and
 * direction are fixed once saved, since the stored settings depend on them. Grinders are never deleted here since
 * shot history refers to them.
 */
export function GrindersDialog(props: { open: boolean; vm: DashboardViewModel; onClose: () => void }) {
  const [editing, setEditing] = useState<Grinder | null>(null);
  const grinders = props.vm.grinders.value;

  const close = () => { setEditing(null); props.onClose(); };
  const blank: Grinder = { id: null, name: "", minGrind: 0, maxGrind: 40, dialResolution: 1, priorSlopeB: UNKNOWN_SLOPE, referenceSetting: null,
                           dialFormat: "decimal", dialDirection: "higher_coarser", calibratedSince: null };

  return (
    <Modal open={props.open} onClose={close}>
      {editing === null ? (
        <>
          <h2>Grinders</h2>
          <div class="hint">The seed slope is the starting guess for how much one dial step changes the shot time; the app learns each grinder's own value from your beans.</div>
          <ul class="plain-list">
            {grinders.map((g) => (
              <li key={g.id} class="row">
                <div class="grow">
                  <div style={{ fontSize: 14 }}>{g.name}</div>
                  <div class="hint">
                    {formatGrind(g.dialDirection === "higher_finer" ? g.maxGrind : g.minGrind, g)}–{formatGrind(g.dialDirection === "higher_finer" ? g.minGrind : g.maxGrind, g)}
                    {" · "}{g.dialFormat === "macro_micro" ? "step 1 letter" : `step ${g.dialResolution}`}
                    {g.dialDirection === "higher_finer" ? " · higher is finer" : ""} · seed {g.priorSlopeB}
                  </div>
                </div>
                <button class="ghost" type="button" onClick={() => setEditing(g)}>Edit</button>
              </li>
            ))}
          </ul>
          <div class="actions">
            <button class="secondary" type="button" onClick={close}>Close</button>
            <button type="button" onClick={() => setEditing(blank)}>Add grinder</button>
          </div>
        </>
      ) : (
        <GrinderForm grinder={editing} vm={props.vm} onDone={() => setEditing(null)} />
      )}
    </Modal>
  );
}

function GrinderForm(props: { grinder: Grinder; vm: DashboardViewModel; onDone: () => void }) {
  const g = props.grinder;
  const adding = g.id === null;
  const [name, setName] = useState(g.name);
  const [preset, setPreset] = useState("unknown");
  const [format, setFormat] = useState<DialFormat>(g.dialFormat);
  const [direction, setDirection] = useState<DialDirection>(g.dialDirection);
  const [min, setMin] = useState(String(g.minGrind));
  const [max, setMax] = useState(String(g.maxGrind));
  const [step, setStep] = useState(String(g.dialResolution));
  const [slope, setSlope] = useState(String(g.priorSlopeB));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const macro = format === "macro_micro";
  const chosen = presetById(preset);

  const pickPreset = (id: string) => {
    const p = presetById(id);
    setPreset(id);
    setSlope(String(p.slope));
    if (p.dial) {
      setFormat(p.dial.format);
      setMin(String(p.dial.min));
      setMax(String(p.dial.max));
      setStep(p.dial.format === "macro_micro" ? "1" : String(p.dial.step));
      if (!name.trim() && !p.rough) setName(p.label);
    }
  };

  const save = async () => {
    const toNum = (s: string) => parseNumber(s) ?? Number.NaN;
    setSaving(true);
    try {
      await props.vm.saveGrinder({
        ...g, name: name.trim(), minGrind: toNum(min), maxGrind: toNum(max),
        dialResolution: macro ? 1 / 9 : toNum(step), priorSlopeB: toNum(slope),
        dialFormat: adding ? format : g.dialFormat, dialDirection: adding ? direction : g.dialDirection,
      });
      props.onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, value: string, set: (v: string) => void, mode: "text" | "decimal" = "decimal") => (
    <label class="field">{label}
      <input type="text" inputMode={mode} value={value} onInput={(e) => set((e.currentTarget as HTMLInputElement).value)} />
    </label>
  );

  return (
    <>
      <h2>{adding ? "Add grinder" : "Edit grinder"}</h2>
      {field("Name", name, setName, "text")}
      {adding ? (
        <>
          <label class="field">Grinder type (sets the seed slope)
            <select value={preset} onChange={(e) => pickPreset((e.currentTarget as HTMLSelectElement).value)}>
              {GRINDER_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <div class="hint" data-testid="seed-hint">
            {preset === "unknown"
              ? `Seed slope ${UNKNOWN_SLOPE.toFixed(2)}. A calibration sweep is recommended: pull three shots across the dial with your first bean.`
              : `Seed slope ${chosen.slope} per dial step${chosen.rough ? " (rough)" : ""}. The app learns your grinder's own value after a few beans.`}
          </div>
          <div class="grid2">
            <label class="field">Dial format
              <select value={format} onChange={(e) => setFormat((e.currentTarget as HTMLSelectElement).value as DialFormat)}>
                <option value="decimal">Numbers (e.g. 7.4)</option>
                <option value="macro_micro">Number + letter A–I (e.g. 9E)</option>
              </select>
            </label>
            <label class="field">Coarser is
              <select value={direction} onChange={(e) => setDirection((e.currentTarget as HTMLSelectElement).value as DialDirection)}>
                <option value="higher_coarser">Higher numbers</option>
                <option value="higher_finer">Lower numbers</option>
              </select>
            </label>
          </div>
        </>
      ) : (
        <div class="hint" data-testid="dial-format">
          Dial format: {g.dialFormat === "macro_micro" ? "macro / micro (e.g. 9E, letters A–I)" : "decimal"}
          {" · "}{g.dialDirection === "higher_finer" ? "higher numbers are finer" : "higher numbers are coarser"}
        </div>
      )}
      <div class="grid2">
        {field(macro ? "Lowest number" : "Min setting", min, setMin)}
        {field(macro ? "Highest number" : "Max setting", max, setMax)}
        {macro ? <div class="field">Dial step<div class="hint" style={{ paddingTop: 12 }}>one letter</div></div> : field("Dial step", step, setStep)}
        {field("Seed slope (negative)", slope, setSlope)}
      </div>
      {error && <div class="error">{error}</div>}
      <div class="actions">
        <button class="secondary" type="button" onClick={props.onDone}>Back</button>
        <button type="button" onClick={() => void save()} disabled={saving}>Save</button>
      </div>
    </>
  );
}
